import { ref, nextTick } from 'vue'

export interface AnimationOptions {
  duration?: number
  easing?: string
  scale?: number
  color?: string
  pulseCount?: number
}

export function useUpdateAnimations() {
  const animatingElements = ref<Map<string, boolean>>(new Map())

  /**
   * Pulse animation for new data
   */
  const pulseElement = async (
    elementId: string,
    options: AnimationOptions = {}
  ) => {
    const {
      duration = 600,
      scale = 1.05,
      color = '#10b981',
      pulseCount = 1
    } = options

    const element = document.getElementById(elementId)
    if (!element) return

    // Mark as animating
    animatingElements.value.set(elementId, true)

    // Store original styles
    const originalTransform = element.style.transform
    const originalBoxShadow = element.style.boxShadow
    const originalTransition = element.style.transition

    // Apply animation
    element.style.transition = `all ${duration}ms cubic-bezier(0.4, 0, 0.2, 1)`

    for (let i = 0; i < pulseCount; i++) {
      // Scale up with glow
      element.style.transform = `scale(${scale})`
      element.style.boxShadow = `0 0 20px ${color}40, 0 0 40px ${color}20`

      await new Promise(resolve => setTimeout(resolve, duration / 2))

      // Scale back down
      element.style.transform = originalTransform || 'scale(1)'
      element.style.boxShadow = originalBoxShadow || 'none'

      await new Promise(resolve => setTimeout(resolve, duration / 2))
    }

    // Restore original styles
    element.style.transition = originalTransition || ''

    // Remove from animating set
    animatingElements.value.delete(elementId)
  }

  /**
   * Flash animation for value changes
   */
  const flashValue = async (
    elementId: string,
    options: AnimationOptions = {}
  ) => {
    const {
      duration = 300,
      color = '#3b82f6'
    } = options

    const element = document.getElementById(elementId)
    if (!element) return

    // Store original background
    const originalBg = element.style.backgroundColor
    const originalColor = element.style.color

    // Flash effect
    element.style.transition = `all ${duration}ms ease-in-out`
    element.style.backgroundColor = `${color}20`
    element.style.color = color

    await new Promise(resolve => setTimeout(resolve, duration))

    // Restore original
    element.style.backgroundColor = originalBg || ''
    element.style.color = originalColor || ''
  }

  /**
   * Slide in animation for new items
   */
  const slideIn = async (
    elementId: string,
    direction: 'left' | 'right' | 'up' | 'down' = 'left',
    options: AnimationOptions = {}
  ) => {
    const {
      duration = 400,
      easing = 'cubic-bezier(0.4, 0, 0.2, 1)'
    } = options

    const element = document.getElementById(elementId)
    if (!element) return

    // Set initial position
    const transforms = {
      left: 'translateX(-100%)',
      right: 'translateX(100%)',
      up: 'translateY(-100%)',
      down: 'translateY(100%)'
    }

    element.style.transform = transforms[direction]
    element.style.opacity = '0'
    element.style.transition = `all ${duration}ms ${easing}`

    // Force reflow
    element.offsetHeight

    // Animate to final position
    await nextTick()
    element.style.transform = 'translate(0)'
    element.style.opacity = '1'

    // Clean up after animation
    setTimeout(() => {
      element.style.transform = ''
      element.style.transition = ''
    }, duration)
  }

  /**
   * Highlight border for updates
   */
  const highlightBorder = async (
    elementId: string,
    options: AnimationOptions = {}
  ) => {
    const {
      duration = 1000,
      color = '#10b981'
    } = options

    const element = document.getElementById(elementId)
    if (!element) return

    const originalBorder = element.style.border

    // Add highlight border
    element.style.transition = `border ${duration}ms ease-in-out`
    element.style.border = `2px solid ${color}`

    await new Promise(resolve => setTimeout(resolve, duration))

    // Fade out border
    element.style.borderColor = 'transparent'

    await new Promise(resolve => setTimeout(resolve, 300))

    // Restore original
    element.style.border = originalBorder || ''
    element.style.transition = ''
  }

  /**
   * Number increment animation
   */
  const animateNumber = async (
    elementId: string,
    from: number,
    to: number,
    options: { duration?: number, decimals?: number } = {}
  ) => {
    const {
      duration = 500,
      decimals = 0
    } = options

    const element = document.getElementById(elementId)
    if (!element) return

    const startTime = Date.now()
    const diff = to - from

    const animate = () => {
      const elapsed = Date.now() - startTime
      const progress = Math.min(elapsed / duration, 1)

      // Easing function
      const easeOutQuart = 1 - Math.pow(1 - progress, 4)
      const current = from + (diff * easeOutQuart)

      element.textContent = current.toFixed(decimals)

      if (progress < 1) {
        requestAnimationFrame(animate)
      } else {
        element.textContent = to.toFixed(decimals)
      }
    }

    requestAnimationFrame(animate)
  }

  /**
   * Progress bar animation
   */
  const animateProgress = async (
    elementId: string,
    toValue: number,
    options: AnimationOptions = {}
  ) => {
    const {
      duration = 800,
      color = '#3b82f6'
    } = options

    const element = document.getElementById(elementId) as HTMLElement
    if (!element) return

    // Ensure it's a progress element or div
    element.style.transition = `width ${duration}ms cubic-bezier(0.4, 0, 0.2, 1)`
    element.style.width = `${Math.min(100, Math.max(0, toValue))}%`

    if (color) {
      element.style.backgroundColor = color
    }
  }

  /**
   * Ripple effect from click point
   */
  const rippleEffect = (
    event: MouseEvent,
    elementId: string,
    options: AnimationOptions = {}
  ) => {
    const {
      duration = 600,
      color = '#ffffff'
    } = options

    const element = document.getElementById(elementId)
    if (!element) return

    const rect = element.getBoundingClientRect()
    const x = event.clientX - rect.left
    const y = event.clientY - rect.top

    const ripple = document.createElement('span')
    ripple.style.position = 'absolute'
    ripple.style.left = `${x}px`
    ripple.style.top = `${y}px`
    ripple.style.width = '0'
    ripple.style.height = '0'
    ripple.style.borderRadius = '50%'
    ripple.style.backgroundColor = color
    ripple.style.opacity = '0.3'
    ripple.style.transform = 'translate(-50%, -50%)'
    ripple.style.pointerEvents = 'none'
    ripple.style.transition = `all ${duration}ms ease-out`

    element.style.position = 'relative'
    element.style.overflow = 'hidden'
    element.appendChild(ripple)

    // Trigger animation
    requestAnimationFrame(() => {
      ripple.style.width = '200px'
      ripple.style.height = '200px'
      ripple.style.opacity = '0'
    })

    // Clean up
    setTimeout(() => {
      ripple.remove()
    }, duration)
  }

  /**
   * Shake animation for errors
   */
  const shake = async (
    elementId: string,
    options: AnimationOptions = {}
  ) => {
    const {
      duration = 500
    } = options

    const element = document.getElementById(elementId)
    if (!element) return

    element.style.animation = `shake ${duration}ms`

    // Add shake keyframes if not exists
    if (!document.getElementById('shake-keyframes')) {
      const style = document.createElement('style')
      style.id = 'shake-keyframes'
      style.textContent = `
        @keyframes shake {
          0%, 100% { transform: translateX(0); }
          10%, 30%, 50%, 70%, 90% { transform: translateX(-5px); }
          20%, 40%, 60%, 80% { transform: translateX(5px); }
        }
      `
      document.head.appendChild(style)
    }

    setTimeout(() => {
      element.style.animation = ''
    }, duration)
  }

  /**
   * Batch animate multiple elements
   */
  const batchAnimate = async (
    animations: Array<{
      elementId: string
      type: 'pulse' | 'flash' | 'highlight' | 'slideIn'
      options?: AnimationOptions
    }>
  ) => {
    const promises = animations.map(({ elementId, type, options }) => {
      switch (type) {
        case 'pulse':
          return pulseElement(elementId, options)
        case 'flash':
          return flashValue(elementId, options)
        case 'highlight':
          return highlightBorder(elementId, options)
        case 'slideIn':
          return slideIn(elementId, 'left', options)
        default:
          return Promise.resolve()
      }
    })

    await Promise.all(promises)
  }

  return {
    animatingElements,
    pulseElement,
    flashValue,
    slideIn,
    highlightBorder,
    animateNumber,
    animateProgress,
    rippleEffect,
    shake,
    batchAnimate
  }
}